// Completing "Review baseline labs" with the care team's note (spec 2026-10-14-lab-results-care-team-note §3.8).
// WF5 answers before WF17 runs, so a refused note is invisible to the browser: the task is read back to know.

import { sendLabReviewCompletion, getTasksByTemplateCode, type CompletionActor } from '../endpoints';
import { labNoteOf } from '@/logic/labs';
import { labNoteClean, labNoteOk } from '@/logic/labResults';

export interface CompleteLabReviewOpts {
  labNote: string;
  notes?: string;
  actor?: CompletionActor;
}

export interface CompleteLabReviewResult {
  completed: boolean;
  noteSaved: boolean;
  // Sent, but the read-back ran out before the task showed Done: sending again could send a second note.
  unconfirmed?: boolean;
  // The send itself failed (apiv2 can time out after forwarding): the task was read back anyway.
  sendError?: string;
}

interface Polling {
  attempts?: number;
  waitMs?: number;
  readTimeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

const LABS_REVIEW = 'onb_baseline_labs_review';
const pause = (ms: number) => new Promise<void>((r) => { setTimeout(r, ms); });

// A hung read counts as "not visible yet": the transport has no timeout of its own.
function within<T>(p: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((r) => { timer = setTimeout(() => r(null), ms); });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
}

export async function completeLabReview(
  taskId: string | number,
  withingsUserId: string | number,
  { labNote, notes = '', actor }: CompleteLabReviewOpts,
  {
    attempts = 7, waitMs = 1500, readTimeoutMs = 4000, sleep = pause,
  }: Polling = {},
): Promise<CompleteLabReviewResult> {
  const text = labNoteClean(labNote);
  // The completion releases the results and cannot be undone: never send it with a note WF17 would drop.
  if (!labNoteOk(text)) throw new Error('The note is empty or over the limits');
  let sendError: string | undefined;
  try {
    const ack = await sendLabReviewCompletion(taskId, withingsUserId, { labNote: text, notes, actor });
    // An older WF5 hands the event to WF26, which answers validation_error: nothing was done.
    if (!ack || ack.status !== 'received') return { completed: false, noteSaved: false };
  } catch (e) {
    sendError = e instanceof Error ? e.message : String(e);
  }
  const unknown = sendError === undefined ? { unconfirmed: true } : { unconfirmed: true, sendError };
  // One read at a time, a pause before each: WF17 runs after WF5 has answered.
  const poll = async (left: number): Promise<CompleteLabReviewResult> => {
    if (left <= 0) return { completed: false, noteSaved: false, ...unknown };
    await sleep(waitMs);
    const rows = await within(getTasksByTemplateCode(withingsUserId, LABS_REVIEW), readTimeoutMs).catch(() => null);
    const row = (rows ?? []).find((t) => String(t.task_id) === String(taskId));
    // The note lands in the same patch as the completion: once Done is visible, the answer is final.
    if (row && row.status === 'completed') {
      const note = labNoteOf(row);
      // WF72 may already have sent it (sentAt set): saved either way.
      return { completed: true, noteSaved: !!note && note.text === text };
    }
    return poll(left - 1);
  };
  return poll(attempts);
}
